export type CourseTermOption = {
  label: string;
  value: string;
  season: "Spring" | "Fall";
  year: number;
};

export type PersistedCourseTerm = {
  label?: string | null;
  season?: string | null;
  year?: number | null;
};

type AcademicTerm = {
  season: "Spring" | "Fall";
  year: number;
};

const normalizeSeason = (season: PersistedCourseTerm["season"]): CourseTermOption["season"] | null => {
  if (season === "Spring" || season === "SPRING") {
    return "Spring";
  }

  if (season === "Fall" || season === "FALL") {
    return "Fall";
  }

  return null;
};

const toCourseTermOption = ({ season, year }: AcademicTerm): CourseTermOption => {
  const label = `${season} ${year}`;
  return { label, value: label, season, year };
};

const parseAcademicTermLabel = (label: string): AcademicTerm | null => {
  const trimmedLabel = label.trim();
  const match = /^(Spring|Fall)\s+(\d{4})$/i.exec(trimmedLabel);

  if (!match) {
    return null;
  }

  const [, rawSeason, rawYear] = match;
  const season = rawSeason.toLowerCase() === "spring" ? "Spring" : "Fall";
  const year = Number(rawYear);

  if (!Number.isInteger(year)) {
    return null;
  }

  return { season, year };
};

export const getCourseTermOptionFromLabel = (label: string): CourseTermOption | null => {
  const parsedTerm = parseAcademicTermLabel(label);
  if (!parsedTerm) {
    return null;
  }

  return toCourseTermOption(parsedTerm);
};

const compareTermOptions = (left: CourseTermOption, right: CourseTermOption): number => {
  if (left.year !== right.year) {
    return left.year - right.year;
  }

  const seasonOrder: Record<CourseTermOption["season"], number> = { Spring: 0, Fall: 1 };
  return seasonOrder[left.season] - seasonOrder[right.season];
};

const getCurrentSelectableTerm = (referenceDate: Date): AcademicTerm => {
  const month = referenceDate.getMonth();
  const year = referenceDate.getFullYear();

  if (month <= 4) {
    return { season: "Spring", year };
  }

  return { season: "Fall", year };
};

const getNextTerm = ({ season, year }: AcademicTerm): AcademicTerm => {
  if (season === "Spring") {
    return { season: "Fall", year };
  }

  return { season: "Spring", year: year + 1 };
};

export const getAcademicTermOptions = (referenceDate = new Date()): CourseTermOption[] => {
  const terms: AcademicTerm[] = [getCurrentSelectableTerm(referenceDate)];

  while (terms.length < 3) {
    const previousTerm = terms[terms.length - 1];
    terms.push(getNextTerm(previousTerm));
  }

  return terms.map(toCourseTermOption);
};

export const getCourseTermFilterOptions = (
  persistedTerms: PersistedCourseTerm[],
  referenceDate = new Date(),
  options: {
    includeSelectableWindow?: boolean;
  } = {}
): CourseTermOption[] => {
  const includeSelectableWindow = options.includeSelectableWindow ?? true;
  const optionsByValue = new Map<string, CourseTermOption>();

  persistedTerms.forEach((term) => {
    if (!term.label) {
      return;
    }

    const season = normalizeSeason(term.season);
    if (season && typeof term.year === "number") {
      const option = toCourseTermOption({ season, year: term.year });
      optionsByValue.set(option.value, option);
      return;
    }

    const optionFromLabel = getCourseTermOptionFromLabel(term.label);
    if (!optionFromLabel) {
      return;
    }

    optionsByValue.set(optionFromLabel.value, optionFromLabel);
  });

  if (includeSelectableWindow) {
    getAcademicTermOptions(referenceDate).forEach((option) => optionsByValue.set(option.value, option));
  }

  return Array.from(optionsByValue.values()).sort(compareTermOptions);
};
